/**
 * Location naming and validation.
 *
 * Kept in `shared/` rather than in the form component because the same rules
 * have to hold in Cloud Functions (invites, seeding) and in tests. A validation
 * rule that only exists in a React component is a rule the backend does not
 * have.
 */
export const MAX_LOCATION_NAME = 40;
/**
 * Onboarding offers these as one-tap chips. Two taps to a working location is
 * the target — see docs/product/onboarding.md; every extra field here is a
 * place a first-time user stalls.
 */
export const LOCATION_PRESETS = [
    { name: "Home", type: "home" },
    { name: "Office", type: "office" },
];
/**
 * What every new user gets, without being asked.
 *
 * Naming a household is a settings task, not an onboarding task — you do it
 * once or never, so it must not stand in front of the product.
 */
export const DEFAULT_HOUSEHOLD = { name: "Home", type: "home" };
/**
 * The type is DERIVED from the name, never held separately. Two sources of
 * truth for "is this the office" is how you end up with a household called
 * Office typed as home. Anything that is not a preset is "other".
 */
export function locationTypeFor(name) {
    const key = normaliseLocationName(name).toLowerCase();
    return LOCATION_PRESETS.find((p) => p.name.toLowerCase() === key)?.type ?? "other";
}
/** The key two names are compared on: "  home" and "Home" are the same place. */
export function locationNameKey(raw) {
    return normaliseLocationName(raw).toLowerCase();
}
/** Collapse runs of whitespace and trim. "  my   flat " -> "my flat". */
export function normaliseLocationName(raw) {
    return raw.replace(/\s+/g, " ").trim();
}
/**
 * `taken` is the names this account already has. Two places called "Home"
 * is not a naming quirk, it is a trap: the switcher shows two identical rows,
 * the new one becomes the default, and the next open looks like every item
 * vanished. So a name must be unique per person, compared loosely.
 */
export function validateLocationName(raw, taken = []) {
    const name = normaliseLocationName(raw);
    if (name.length === 0)
        return "empty";
    if (name.length > MAX_LOCATION_NAME)
        return "too_long";
    const key = name.toLowerCase();
    if (taken.some((t) => locationNameKey(t) === key))
        return "taken";
    return null;
}
export const NAME_PROBLEM_TEXT = {
    empty: "Give it a name",
    too_long: `Keep it under ${MAX_LOCATION_NAME} characters`,
    taken: "You already have a place with that name",
};
/**
 * The device zone is right for essentially every real user, and asking would
 * cost a screen. Falls back to Asia/Kolkata (the only market V1 targets) when
 * the runtime cannot say — never to UTC, which would fire check-ins at 5:30am.
 */
export function deviceTimezone() {
    try {
        return Intl.DateTimeFormat().resolvedOptions().timeZone || "Asia/Kolkata";
    }
    catch {
        return "Asia/Kolkata";
    }
}
/** Throws on invalid input — callers validate first and show `NAME_PROBLEM_TEXT`. */
export function prepareNewLocation(input, taken = []) {
    const problem = validateLocationName(input.name, taken);
    if (problem)
        throw new Error(`invalid location name: ${problem}`);
    return {
        name: normaliseLocationName(input.name),
        type: input.type,
        timezone: input.timezone ?? deviceTimezone(),
    };
}
/**
 * Throws on invalid input, like `prepareNewLocation`. Only the name moves;
 * the type follows it, and the timezone is left alone — the place did not move
 * cities because it got a new name.
 */
export function prepareRename(raw, taken = []) {
    const problem = validateLocationName(raw, taken);
    if (problem)
        throw new Error(`invalid location name: ${problem}`);
    const name = normaliseLocationName(raw);
    return { name, type: locationTypeFor(name) };
}
//# sourceMappingURL=location.js.map